import {
  ActionError,
  AttentionBlock,
  Button,
  FormBlock,
  ListRow,
  ListRows,
  PageHeader,
  PageTitle,
  ReadingColumn,
  StepProgress,
  plural,
  toast,
} from '@westhetics/ui'
import { useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { Page } from '@/app/shell'
import { useCatalogOptions } from '@/features/catalog/api'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import {
  useImportProcedures,
  useValidateProcedureImport,
  type ProcedureImportFile,
  type ProcedureImportValidation,
} from './api'
import {
  IMPORT_MAX_BYTES,
  IMPORT_TEMPLATE_FILE_NAME,
  buildImportTemplate,
  importTemplateHref,
  readCsvFile,
} from './import-file'

/**
 * Importar procedimentos por CSV (docs/areas/administracao.md, "Importar por CSV"; RN-ADM-37 e
 * RN-ADM-76). Arquétipo: fluxo em etapas. A pessoa escolhe o arquivo, a API confere sem gravar e,
 * com o arquivo inteiro certo, a segunda etapa importa tudo de uma vez. Com problema em uma linha,
 * nada é gravado e a lista diz a linha, a coluna, a causa e o caminho.
 */

/** O que vai em cada coluna do modelo, na ordem em que a pessoa preenche. */
const COLUMN_GUIDE: [columns: string, rule: string][] = [
  [
    'procedimento',
    'O nome na lista da Westhetics, ou um sinônimo dela. É ele que traz os termos que a paciente assina. Para juntar vários num procedimento só, separe por | com as aplicações, como Toxina botulínica x2 | Bioestimulador de colágeno.',
  ],
  [
    'nome · opcional',
    'O nome que a clínica usa e que sai no contrato, sem vírgula. Vazio, vale o nome da lista, ou os nomes ligados por +.',
  ],
  ['valor', 'Em reais, como 1.500,00.'],
  ['meios', 'pix, debito, credito e dinheiro, separados por espaço.'],
  [
    'parcelas · juros',
    'Só com crédito: até quantas vezes, de 1 a 24, e sem ou com juros. Vazio, vale sem.',
  ],
  [
    'desconto · descontoMeios · opcional',
    'O desconto à vista em porcentagem e os meios em que ele vale, como 10 e pix.',
  ],
  [
    'vigencia · opcional',
    'Os meses de validade, contados da assinatura, quando há prazo, como 12.',
  ],
  ['descricao · opcional', 'O que o procedimento inclui e as regras dele.'],
]

const MSG_TOO_BIG = 'O arquivo passa de 1 MB. Divida em arquivos menores.'
const MSG_UNREADABLE =
  'O navegador não conseguiu abrir o arquivo. Confira se ele ainda está no lugar e escolha de novo.'

/** "30 avulsos e 12 clubes ou pacotes": só os tipos que o arquivo tem. */
function byTypeLabel(byType: ProcedureImportValidation['byType']): string {
  return [
    byType.single > 0 ? plural(byType.single, 'avulso') : null,
    byType.club_or_package > 0
      ? plural(byType.club_or_package, 'clube ou pacote', 'clubes ou pacotes')
      : null,
  ]
    .filter(Boolean)
    .join(' e ')
}

export function ImportProceduresPage() {
  const navigate = useNavigate()
  const options = useCatalogOptions()
  const validate = useValidateProcedureImport()
  const importFile = useImportProcedures()

  const input = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<ProcedureImportFile | null>(null)
  const [isReading, setIsReading] = useState(false)
  /** O que impediu o arquivo de chegar à API: grande demais ou ilegível. */
  const [readError, setReadError] = useState<string | null>(null)

  const templateHref = useMemo(
    () => importTemplateHref(buildImportTemplate(options.data ?? [])),
    [options.data],
  )

  async function choose(event: ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0]
    // Escolher de novo o mesmo arquivo, já corrigido, precisa disparar a mudança.
    event.target.value = ''
    if (!chosen) return
    validate.reset()
    importFile.reset()
    setFile(null)
    setReadError(null)
    if (chosen.size > IMPORT_MAX_BYTES) {
      setReadError(MSG_TOO_BIG)
      return
    }
    setIsReading(true)
    try {
      const next = { fileName: chosen.name, content: await readCsvFile(chosen) }
      setFile(next)
      validate.mutate(next)
    } catch {
      setReadError(MSG_UNREADABLE)
    } finally {
      setIsReading(false)
    }
  }

  const validation = validate.data
  const ready = file && validation?.isValid ? { file, validation } : null
  const problems = validation && !validation.isValid ? validation : null
  const isChecking = isReading || validate.isPending
  // 422 é o arquivo que precisa de correção; qualquer outra falha se resolve tentando de novo.
  const isRejectedFile = isApiError(validate.error) && validate.error.status === 422
  const needsFix = Boolean(problems || readError || isRejectedFile)

  function backToFile() {
    validate.reset()
    importFile.reset()
    setFile(null)
  }

  function submit(chosen: ProcedureImportFile) {
    importFile.mutate(chosen, {
      onSuccess: (result) => {
        toast(
          result.createdCount === 1
            ? '1 procedimento importado'
            : `${result.createdCount} procedimentos importados`,
        )
        navigate(routes.admin.procedures)
      },
    })
  }

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Procedimentos', href: routes.admin.procedures }, { label: 'importar' }]}
      />
      <Page>
        <ReadingColumn>
          {/* O seletor do navegador nunca aparece: quem abre a escolha é o botão da etapa. */}
          <input
            ref={input}
            type="file"
            accept=".csv,text/csv"
            hidden
            aria-label="Arquivo CSV"
            onChange={(event) => void choose(event)}
          />

          {ready ? (
            <>
              <StepProgress current={2} total={2} label="Confira e importe" />
              <PageTitle
                className="mt-6"
                title={
                  ready.validation.total === 1
                    ? '1 procedimento pronto para importar'
                    : `${ready.validation.total} procedimentos prontos para importar`
                }
                subtitle={`${byTypeLabel(ready.validation.byType)} em ${ready.file.fileName}. Nada foi gravado.`}
              />

              {importFile.isError ? (
                <ActionError
                  className="mt-6"
                  title="Nada foi importado"
                  description={errorMessage(importFile.error)}
                  actions={
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        importFile.reset()
                        validate.mutate(ready.file)
                      }}
                    >
                      Validar de novo
                    </Button>
                  }
                />
              ) : null}

              <AttentionBlock className="mt-6" variant="lilac" title="O que acontece ao importar">
                {ready.validation.total === 1
                  ? 'O procedimento entra no catálogo da clínica com os termos que a Westhetics liga a ele.'
                  : 'Os procedimentos entram no catálogo da clínica de uma vez, cada um com os termos que a Westhetics liga a ele. Se um falhar, nenhum entra.'}{' '}
                Valor, nome e condição de pagamento podem ser ajustados depois, um a um.
              </AttentionBlock>

              <div className="mt-6 flex flex-wrap justify-end gap-3">
                <Button variant="outline" onClick={backToFile} disabled={importFile.isPending}>
                  Voltar
                </Button>
                <Button
                  onClick={() => submit(ready.file)}
                  loading={importFile.isPending}
                  loadingLabel="Importando para o catálogo…"
                >
                  {ready.validation.total === 1
                    ? 'Importar 1 procedimento'
                    : `Importar ${ready.validation.total} procedimentos`}
                </Button>
              </div>
            </>
          ) : (
            <>
              <StepProgress current={1} total={2} label="Escolha o arquivo" />
              <PageTitle
                className="mt-6"
                title="Importar procedimentos de uma planilha"
                subtitle="Para quem já tem a tabela de preços pronta. Cada linha vira um procedimento da clínica, ligado à lista da Westhetics. Nada é gravado antes de você conferir."
              />

              {readError ? (
                <ActionError
                  className="mt-6"
                  title="Não deu para usar este arquivo"
                  description={readError}
                />
              ) : null}

              {validate.isError ? (
                <ActionError
                  className="mt-6"
                  title="Não deu para conferir o arquivo"
                  description={errorMessage(validate.error)}
                  actions={
                    file && !isRejectedFile ? (
                      <Button variant="outline" size="sm" onClick={() => validate.mutate(file)}>
                        Tentar de novo
                      </Button>
                    ) : undefined
                  }
                />
              ) : null}

              {problems ? <Problems validation={problems} fileName={file?.fileName} /> : null}

              <FormBlock
                className="mt-6"
                title="O modelo"
                note={
                  options.isError
                    ? 'Não conseguimos carregar a lista da Westhetics agora: o modelo vai só com os nomes das colunas.'
                    : 'Vem com uma linha para cada procedimento da lista da Westhetics que a clínica ainda não tem. Preencha valor e meios do que a clínica oferece e apague as linhas do resto.'
                }
              >
                {options.isPending ? (
                  <Button variant="outline" loading loadingLabel="Preparando o modelo…">
                    Baixar o modelo
                  </Button>
                ) : (
                  <Button variant="outline" asChild>
                    <a href={templateHref} download={IMPORT_TEMPLATE_FILE_NAME}>
                      Baixar o modelo
                    </a>
                  </Button>
                )}

                <dl className="border-hairline mt-5 border-t">
                  {COLUMN_GUIDE.map(([columns, rule]) => (
                    <div
                      key={columns}
                      className="border-hairline tablet:grid-cols-[200px_minmax(0,1fr)] grid grid-cols-1 gap-x-5 gap-y-1 border-b py-3"
                    >
                      <dt className="font-display text-ink text-[13px] leading-[1.5] font-normal">
                        {columns}
                      </dt>
                      <dd className="font-body text-body text-secondary font-light">{rule}</dd>
                    </div>
                  ))}
                </dl>
              </FormBlock>

              <p className="font-body text-secondary mt-4 text-[12px] leading-[1.6] font-light">
                Arquivo CSV de até 1 MB, com até 1.000 procedimentos. No Excel: Arquivo › Salvar
                como › CSV UTF-8. No Google Sheets: Arquivo › Fazer download › CSV.
              </p>

              <div className="mt-6 flex flex-wrap justify-end gap-3">
                <Button variant="outline" asChild>
                  <Link to={routes.admin.procedures}>Cancelar</Link>
                </Button>
                <Button
                  onClick={() => input.current?.click()}
                  loading={isChecking}
                  loadingLabel="Conferindo o arquivo…"
                >
                  {needsFix ? 'Escolher o arquivo corrigido' : 'Escolher arquivo'}
                </Button>
              </div>
            </>
          )}
        </ReadingColumn>
      </Page>
    </>
  )
}

/** As linhas com problema: nada foi gravado, e cada uma diz a coluna, a causa e o caminho. */
function Problems({
  validation,
  fileName,
}: {
  validation: ProcedureImportValidation
  fileName: string | undefined
}) {
  const rightCount = validation.total - validation.invalidLineCount
  return (
    <>
      <ActionError
        className="mt-6"
        title={
          validation.invalidLineCount === 1
            ? '1 linha tem problema; nada foi importado'
            : `${validation.invalidLineCount} linhas têm problema; nada foi importado`
        }
        description={
          <>
            Corrija na planilha e escolha o arquivo de novo.
            {rightCount > 0
              ? ` ${rightCount === 1 ? 'A outra linha' : `As outras ${rightCount} linhas`} de ${fileName ?? 'seu arquivo'} ${rightCount === 1 ? 'está certa' : 'estão certas'}.`
              : null}
          </>
        }
      />
      <ListRows className="mt-3" role="list" aria-label="Linhas com problema">
        {validation.errors.map((error) => (
          <ListRow
            key={`${error.line}.${error.field ?? ''}`}
            role="listitem"
            title={error.field ? `Linha ${error.line} · ${error.field}` : `Linha ${error.line}`}
            meta={error.message}
          />
        ))}
        {validation.omittedErrorCount > 0 ? (
          <ListRow
            role="listitem"
            title={
              validation.omittedErrorCount === 1
                ? 'Mais 1 problema'
                : `Mais ${validation.omittedErrorCount} problemas`
            }
            meta="Aparecem aqui depois que os primeiros forem corrigidos."
          />
        ) : null}
      </ListRows>
    </>
  )
}
